"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type HyroxDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * Race day in the HYROX hall: lighting truss and a glowing LED board across
 * the back wall, a turf sled lane running in perspective to a loaded sled
 * caught in two crossing spots (the focal point). The wall-ball rig holds the
 * left flank, a sandbag and kettlebells the right. The orange start line sits
 * under the participant pill.
 *
 * Motion: a sheen sweeps the LED board and the spots breathe — CSS transform
 * and opacity only, off under reduced motion.
 */
const HYROX_KEYFRAMES = `
@keyframes hyrox-sheen {
  0% { transform: translate3d(-120%, 0, 0) }
  60%, 100% { transform: translate3d(320%, 0, 0) }
}
@keyframes hyrox-beam { 0%, 100% { opacity: 0.75 } 50% { opacity: 1 } }
.hyrox-sheen { animation: hyrox-sheen 6s ease-in-out infinite }
.hyrox-beam { animation: hyrox-beam 4.5s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .hyrox-sheen { animation: none; opacity: 0 }
  .hyrox-beam { animation: none }
}
`;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `hyrox-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

const ORANGE = "#ff7a1a";

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id("turf")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#3d6b4b" />
          <stop offset="0.45" stopColor="#2c573a" />
          <stop offset="1" stopColor="#173322" />
        </linearGradient>
        <linearGradient id={id("beam")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#ffe2b8" stopOpacity="0.38" />
          <stop offset="0.8" stopColor="#ffd09a" stopOpacity="0.06" />
          <stop offset="1" stopColor="#ffd09a" stopOpacity="0" />
        </linearGradient>
        <radialGradient id={id("pool")} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#ffd9a6" stopOpacity="0.5" />
          <stop offset="1" stopColor="#ffd9a6" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={id("frame")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#ffb066" />
          <stop offset="0.45" stopColor={ORANGE} />
          <stop offset="1" stopColor="#a8420a" />
        </linearGradient>
        <linearGradient id={id("steel")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#a8adb6" />
          <stop offset="1" stopColor="#3a3d44" />
        </linearGradient>
        <radialGradient id={id("ball")} cx="0.35" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#5a5a62" />
          <stop offset="0.6" stopColor="#1d1d22" />
          <stop offset="1" stopColor="#0a0a0c" />
        </radialGradient>
        <radialGradient id={id("bell")} cx="0.35" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#6b6f78" />
          <stop offset="0.55" stopColor="#24262c" />
          <stop offset="1" stopColor="#0b0b0e" />
        </radialGradient>
        <linearGradient id={id("sandbag")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#4a4d55" />
          <stop offset="1" stopColor="#17181c" />
        </linearGradient>
      </defs>
    </svg>
  );
}

const PLATES = [
  { color: "#d1172b", side: "#7a0a16", y: 145 },
  { color: "#1f63d6", side: "#0c2d6e", y: 138 },
  { color: "#1f63d6", side: "#0c2d6e", y: 131 },
  { color: "#f2c230", side: "#8a6a06", y: 124 },
];

function Sled({ id }: { id: IdFn }) {
  return (
    <g>
      {/* Contact shadow */}
      <ellipse cx="150" cy="158" rx="50" ry="7" fill="#050807" opacity="0.6" />
      {/* Push poles */}
      <path
        d="M120 150 L106 94 M180 150 L194 94"
        stroke={`url(#${id("frame")})`}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M119 146 L106.5 96"
        stroke="#ffd2a8"
        strokeOpacity="0.8"
        strokeWidth="1"
      />
      {/* Grips */}
      <path
        d="M106 94 L103 82 M194 94 L197 82"
        stroke="#141416"
        strokeWidth="6"
        strokeLinecap="round"
      />
      {/* Skid base */}
      <polygon
        points="110,148 190,148 198,157 102,157"
        fill={`url(#${id("steel")})`}
      />
      <polygon
        points="110,148 190,148 192,150 108,150"
        fill="#d7dbe2"
        opacity="0.7"
      />
      <rect x="100" y="156" width="100" height="3" rx="1.5" fill="#101114" />
      {/* Weight horn */}
      <rect
        x="146.5"
        y="112"
        width="7"
        height="38"
        rx="2"
        fill={`url(#${id("steel")})`}
      />
      {/* Plates, bottom to top: side band + top face */}
      {PLATES.map((plate) => (
        <g key={`plate-${plate.y}`}>
          <rect
            x="124"
            y={plate.y - 3}
            width="52"
            height="6"
            fill={plate.side}
          />
          <ellipse
            cx="150"
            cy={plate.y + 3}
            rx="26"
            ry="5.5"
            fill={plate.side}
          />
          <rect
            x="124"
            y={plate.y + 2}
            width="52"
            height="1"
            fill="#000"
            opacity="0.35"
          />
          <ellipse
            cx="150"
            cy={plate.y - 3}
            rx="26"
            ry="5.5"
            fill={plate.color}
          />
          <ellipse
            cx="150"
            cy={plate.y - 3}
            rx="18"
            ry="3.6"
            fill="#000000"
            opacity="0.22"
          />
          <path
            d={`M126 ${plate.y - 4} Q138 ${plate.y - 9.5} 150 ${plate.y - 8.5}`}
            stroke="#ffffff"
            strokeOpacity="0.5"
            strokeWidth="1"
            fill="none"
          />
        </g>
      ))}
      <ellipse cx="150" cy="113" rx="3.5" ry="1.4" fill="#e6e9ee" />
    </g>
  );
}

function Hall({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-0 left-1/2 -translate-x-1/2"
      width="300"
      height="220"
      viewBox="0 0 300 220"
      aria-hidden="true"
      focusable="false"
    >
      {/* Turf sled lane */}
      <polygon
        points="122,96 178,96 280,220 20,220"
        fill={`url(#${id("turf")})`}
      />
      <path
        d="M126 96 L32 220 M174 96 L268 220"
        stroke="#f4f4f0"
        strokeOpacity="0.75"
        strokeWidth="1.6"
      />
      {[104, 116, 134, 162].map((y) => {
        const t = (y - 96) / 124;
        const half = 28 + t * 130 - 4 - t * 8;
        return (
          <line
            key={`mark-${y}`}
            x1={150 - half}
            x2={150 + half}
            y1={y}
            y2={y}
            stroke="#f4f4f0"
            strokeOpacity={0.25 + t * 0.3}
            strokeWidth={0.6 + t * 1.2}
          />
        );
      })}
      {/* Start line, under the participant pill */}
      <line
        x1="38"
        x2="262"
        y1="204"
        y2="204"
        stroke={ORANGE}
        strokeWidth="3"
      />

      {/* Spots crossing on the sled */}
      <g className="hyrox-beam">
        <polygon
          points="36,14 52,14 176,156 112,160"
          fill={`url(#${id("beam")})`}
        />
        <polygon
          points="248,14 264,14 188,160 124,156"
          fill={`url(#${id("beam")})`}
        />
      </g>
      <ellipse cx="150" cy="148" rx="74" ry="22" fill={`url(#${id("pool")})`} />

      <Sled id={id} />

      {/* Left flank: wall-ball rig + ball */}
      <rect x="10" y="22" width="6" height="168" fill="#1c1d22" />
      <rect
        x="11"
        y="22"
        width="1.4"
        height="168"
        fill="#6a6e78"
        opacity="0.6"
      />
      <rect x="10" y="40" width="52" height="5" fill="#1c1d22" />
      <circle
        cx="44"
        cy="64"
        r="12"
        fill="none"
        stroke={ORANGE}
        strokeWidth="3"
      />
      <circle cx="44" cy="64" r="5" fill={ORANGE} opacity="0.9" />
      <line x1="44" x2="44" y1="45" y2="52" stroke="#2a2b31" strokeWidth="2" />
      <ellipse cx="40" cy="198" rx="17" ry="4" fill="#000" opacity="0.5" />
      <circle cx="40" cy="184" r="14" fill={`url(#${id("ball")})`} />
      <path
        d="M27 182 Q40 176 53 182 M40 170 Q35 184 40 198"
        stroke={ORANGE}
        strokeOpacity="0.85"
        strokeWidth="1.3"
        fill="none"
      />
      <path
        d="M30 176 Q36 171 43 171"
        stroke="#ffffff"
        strokeOpacity="0.35"
        strokeWidth="1.2"
        fill="none"
      />

      {/* Right flank: sandbag + kettlebells */}
      <ellipse cx="262" cy="160" rx="26" ry="4" fill="#000" opacity="0.5" />
      <rect
        x="238"
        y="140"
        width="48"
        height="19"
        rx="9.5"
        fill={`url(#${id("sandbag")})`}
      />
      <rect x="252" y="140" width="4" height="19" fill={ORANGE} opacity="0.9" />
      <rect x="268" y="140" width="4" height="19" fill={ORANGE} opacity="0.9" />
      <path
        d="M244 143 Q262 139 280 143"
        stroke="#ffffff"
        strokeOpacity="0.25"
        strokeWidth="1"
        fill="none"
      />
      {[
        { x: 252, y: 190, r: 11 },
        { x: 279, y: 196, r: 13 },
      ].map((bell) => (
        <g key={`bell-${bell.x}`}>
          <ellipse
            cx={bell.x}
            cy={bell.y + bell.r - 1}
            rx={bell.r + 3}
            ry="3"
            fill="#000"
            opacity="0.5"
          />
          <path
            d={`M${bell.x - bell.r * 0.6} ${bell.y - bell.r * 0.6} Q${bell.x - bell.r * 0.7} ${bell.y - bell.r * 1.7} ${bell.x} ${bell.y - bell.r * 1.7} Q${bell.x + bell.r * 0.7} ${bell.y - bell.r * 1.7} ${bell.x + bell.r * 0.6} ${bell.y - bell.r * 0.6}`}
            stroke="#2a2c33"
            strokeWidth={bell.r * 0.3}
            fill="none"
          />
          <circle
            cx={bell.x}
            cy={bell.y}
            r={bell.r}
            fill={`url(#${id("bell")})`}
          />
          <path
            d={`M${bell.x - bell.r * 0.8} ${bell.y - bell.r * 0.35} Q${bell.x} ${bell.y - bell.r * 0.6} ${bell.x + bell.r * 0.8} ${bell.y - bell.r * 0.35}`}
            stroke={ORANGE}
            strokeWidth="2"
            fill="none"
          />
        </g>
      ))}
    </svg>
  );
}

export function HyroxDisplayPreview({
  participants,
}: HyroxDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Hyrox"
      backgroundClassName="bg-[#09090b]"
      participants={participants}
      scene={
        <>
          <style href="hyrox-scene-keyframes" precedence="default">
            {HYROX_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Hall */}
          <div className="absolute inset-0 bg-[linear-gradient(180deg,#141318_0%,#0e0d11_42%,#18171b_44%,#0c0c0e_100%)]" />
          {/* LED board across the back wall */}
          <div
            className="absolute inset-x-0 top-[26px] h-[44px] overflow-hidden"
            style={{
              background:
                "repeating-linear-gradient(115deg, rgba(0,0,0,0) 0 18px, rgba(0,0,0,0.28) 18px 30px), linear-gradient(90deg, #2a0e04 0%, #c84a10 22%, #ff8a2a 50%, #c84a10 78%, #2a0e04 100%)",
              boxShadow:
                "0 10px 40px rgba(255,122,26,0.35), inset 0 0 0 1px rgba(255,200,140,0.18)",
            }}
          >
            <div className="absolute inset-0 bg-[repeating-linear-gradient(180deg,rgba(0,0,0,0.35)_0_1px,transparent_1px_3px)]" />
            <div className="hyrox-sheen absolute inset-y-0 left-0 w-1/3 bg-[linear-gradient(90deg,transparent,rgba(255,236,200,0.35),transparent)]" />
          </div>
          {/* Stands under the board */}
          <div
            className="absolute inset-x-0 top-[70px] h-[28px] opacity-50"
            style={{
              backgroundImage:
                "radial-gradient(circle at 50% 100%, rgba(255,180,120,0.16) 0 3px, transparent 3.5px)",
              backgroundSize: "9px 9px",
            }}
          />
          {/* Lighting truss */}
          <div
            className="absolute inset-x-0 top-[8px] h-[10px] border-y-2 border-[#3a3b42]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(60deg, transparent 0 6px, #34353c 6px 7.5px), repeating-linear-gradient(-60deg, transparent 0 6px, #34353c 6px 7.5px)",
            }}
          />
          {/* Floor sheen */}
          <div className="absolute inset-x-0 bottom-0 h-[56%] bg-[radial-gradient(70%_60%_at_50%_30%,rgba(255,150,70,0.12)_0%,transparent_70%)]" />

          <Hall id={id} />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_115%_120%_at_50%_45%,transparent_52%,rgba(0,0,0,0.6)_100%)]" />
      }
    />
  );
}
